/**
 * Retreat Participant Info Form
 *
 * Collects logistics and personal-area info for every Costa Rica retreat
 * attendee. Required as part of Step 5 of the welcome page. Includes the
 * full Sister Diamonds App Master Terms of Service and Privacy Policy
 * (Rev 6, May 26 2026) — submission requires explicit agreement.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle2, Plane, MapPin, Shirt, FileText, ArrowLeft } from 'lucide-react';
import { RETREAT_TERMS_TEXT, RETREAT_TERMS_REVISION } from '@/constants/retreatTerms';

const API_URL = window.location.origin;

const TSHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const YES_NO = ['No', 'Yes'];

const FieldLabel = ({ children, required }) => (
  <Label className="text-sm font-medium text-forest">
    {children}
    {required && <span className="text-terracotta ml-0.5">*</span>}
  </Label>
);

const FormSection = ({ icon: Icon, title, children }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 pb-2 border-b border-sand">
      <Icon className="h-5 w-5 text-terracotta" />
      <h3 className="font-heading text-lg text-forest">{title}</h3>
    </div>
    {children}
  </div>
);

const RetreatParticipantFormPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [scrolledTerms, setScrolledTerms] = useState(false);

  const [form, setForm] = useState({
    // Identity (prefilled from user when available)
    full_name: '',
    email: '',
    phone_number: '',
    // Personal
    home_address: '',  // street address
    city: '',
    state: '',
    zip_code: '',
    tshirt_size: '',
    shoe_size: '',
    life_personal_areas: '',  // 3 areas
    professional_areas: '',   // 3 areas
    medical_restrictions: '',
    special_accommodations: '',
    // Outbound travel
    departure_city: '',
    outbound_airline_flight: '',
    outbound_departure_datetime: '',
    outbound_connecting: 'No',
    arrival_in_costa_rica_datetime: '',
    // Return travel
    return_airline_flight: '',
    return_departure_datetime: '',
    return_connecting: 'No',
    home_arrival_date: '',
    // Agreement
    terms_agreed: false,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Prefill identity + check existing submission
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = user
          ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
          : {};
        if (user) {
          const res = await axios.get(`${API_URL}/api/retreat/participant-form/mine`, {
            headers,
          });
          if (cancelled) return;
          if (res.data?.submitted) {
            setAlreadySubmitted(true);
            setForm((f) => ({ ...f, ...(res.data.form || {}) }));
          } else {
            setForm((f) => ({
              ...f,
              full_name: user.full_name || '',
              email: user.email || '',
              phone_number: user.phone_number || '',
            }));
          }
        }
      } catch (e) {
        // Non-fatal — page still works for cold visitors
        console.error('Prefill failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleTermsScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledTerms(true);
    }
  };

  const requiredFields = [
    'full_name', 'email', 'phone_number',
    'home_address', 'city', 'state', 'zip_code',
    'tshirt_size', 'shoe_size',
    'life_personal_areas', 'professional_areas',
    'departure_city', 'outbound_airline_flight',
    'outbound_departure_datetime', 'arrival_in_costa_rica_datetime',
    'return_airline_flight', 'return_departure_datetime', 'home_arrival_date',
  ];

  const missingFields = () =>
    requiredFields.filter((f) => !String(form[f] || '').trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = missingFields();
    if (missing.length > 0) {
      toast.error(`Please complete all required fields (${missing.length} missing).`);
      return;
    }
    if (!form.terms_agreed) {
      toast.error('Please review and agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const headers = user
        ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
        : {};
      await axios.post(
        `${API_URL}/api/retreat/participant-form`,
        { ...form, terms_revision: RETREAT_TERMS_REVISION },
        { headers }
      );
      setAlreadySubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success("We've got your details — see you in Costa Rica!");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sand/30 to-white py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardContent className="p-8 text-center" data-testid="participant-form-submitted">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
              <h1 className="font-heading text-2xl text-forest mb-2">You're all set, Sister.</h1>
              <p className="text-gray-700 mb-6">
                Your travel details and personal info have been received. We'll
                use them to make your retreat as smooth — and as personal — as possible.
              </p>
              <Button asChild className="bg-forest hover:bg-forest/90">
                <Link to="/retreat-welcome">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Welcome Page
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand/30 to-white py-10 px-4">
      <div className="max-w-3xl mx-auto" data-testid="participant-form-page">
        <div className="mb-6">
          <Link to="/retreat-welcome" className="text-sm text-terracotta hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to Welcome
          </Link>
          <h1 className="font-heading text-3xl text-forest mt-3">
            Retreat Participant Information
          </h1>
          <p className="text-gray-600 mt-1">
            A few logistics so we can take great care of you in Costa Rica.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Identity */}
          <Card>
            <CardContent className="p-6">
              <FormSection icon={MapPin} title="About You">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>Full Name</FieldLabel>
                    <Input
                      data-testid="field-full_name"
                      value={form.full_name}
                      onChange={(e) => update('full_name', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>Email</FieldLabel>
                    <Input
                      data-testid="field-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>Phone Number</FieldLabel>
                    <Input
                      data-testid="field-phone_number"
                      value={form.phone_number}
                      onChange={(e) => update('phone_number', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel required>Street Address</FieldLabel>
                    <Input
                      data-testid="field-home_address"
                      placeholder="e.g. 123 Magnolia Lane, Apt 4"
                      value={form.home_address}
                      onChange={(e) => update('home_address', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>City</FieldLabel>
                    <Input
                      data-testid="field-city"
                      placeholder="e.g. Atlanta"
                      value={form.city}
                      onChange={(e) => update('city', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>State / Province</FieldLabel>
                    <Input
                      data-testid="field-state"
                      placeholder="e.g. GA"
                      value={form.state}
                      onChange={(e) => update('state', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel required>Zip / Postal Code</FieldLabel>
                    <Input
                      data-testid="field-zip_code"
                      placeholder="e.g. 30354"
                      value={form.zip_code}
                      onChange={(e) => update('zip_code', e.target.value)}
                    />
                  </div>
                </div>
              </FormSection>
            </CardContent>
          </Card>

          {/* Sizing & focus */}
          <Card>
            <CardContent className="p-6">
              <FormSection icon={Shirt} title="Personal Details">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>T-Shirt Size</FieldLabel>
                    <Select
                      value={form.tshirt_size}
                      onValueChange={(v) => update('tshirt_size', v)}
                    >
                      <SelectTrigger data-testid="field-tshirt_size">
                        <SelectValue placeholder="Select your size" />
                      </SelectTrigger>
                      <SelectContent>
                        {TSHIRT_SIZES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <FieldLabel required>Shoe Size</FieldLabel>
                    <Input
                      data-testid="field-shoe_size"
                      placeholder="e.g. US 8.5 women's"
                      value={form.shoe_size}
                      onChange={(e) => update('shoe_size', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel required>3 Life / Personal Areas you're focused on</FieldLabel>
                    <Textarea
                      data-testid="field-life_personal_areas"
                      rows={3}
                      placeholder="List 3 areas (e.g. health, relationships, spirituality)"
                      value={form.life_personal_areas}
                      onChange={(e) => update('life_personal_areas', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel required>3 Professional Areas you're focused on</FieldLabel>
                    <Textarea
                      data-testid="field-professional_areas"
                      rows={3}
                      placeholder="List 3 areas (e.g. leadership, career pivot, board service)"
                      value={form.professional_areas}
                      onChange={(e) => update('professional_areas', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel>Medical Restrictions</FieldLabel>
                    <Textarea
                      data-testid="field-medical_restrictions"
                      rows={2}
                      placeholder="Allergies, dietary needs, conditions we should know about. Leave blank if none."
                      value={form.medical_restrictions}
                      onChange={(e) => update('medical_restrictions', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel>Special Accommodations</FieldLabel>
                    <Textarea
                      data-testid="field-special_accommodations"
                      rows={2}
                      placeholder="Mobility, sensory, language, or anything else we can support."
                      value={form.special_accommodations}
                      onChange={(e) => update('special_accommodations', e.target.value)}
                    />
                  </div>
                </div>
              </FormSection>
            </CardContent>
          </Card>

          {/* Outbound travel */}
          <Card>
            <CardContent className="p-6">
              <FormSection icon={Plane} title="Outbound Travel to Costa Rica">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>Departure City</FieldLabel>
                    <Input
                      data-testid="field-departure_city"
                      placeholder="e.g. Atlanta, GA"
                      value={form.departure_city}
                      onChange={(e) => update('departure_city', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>Airline &amp; Flight Number</FieldLabel>
                    <Input
                      data-testid="field-outbound_airline_flight"
                      placeholder="e.g. Delta DL 397"
                      value={form.outbound_airline_flight}
                      onChange={(e) => update('outbound_airline_flight', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>Departure Date &amp; Time</FieldLabel>
                    <Input
                      data-testid="field-outbound_departure_datetime"
                      type="datetime-local"
                      value={form.outbound_departure_datetime}
                      onChange={(e) => update('outbound_departure_datetime', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>Are you connecting?</FieldLabel>
                    <Select
                      value={form.outbound_connecting}
                      onValueChange={(v) => update('outbound_connecting', v)}
                    >
                      <SelectTrigger data-testid="field-outbound_connecting">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YES_NO.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel required>Arrival Date &amp; Time in Costa Rica (SJO)</FieldLabel>
                    <Input
                      data-testid="field-arrival_in_costa_rica_datetime"
                      type="datetime-local"
                      value={form.arrival_in_costa_rica_datetime}
                      onChange={(e) => update('arrival_in_costa_rica_datetime', e.target.value)}
                    />
                  </div>
                </div>
              </FormSection>
            </CardContent>
          </Card>

          {/* Return travel */}
          <Card>
            <CardContent className="p-6">
              <FormSection icon={Plane} title="Return Travel">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required>Return Airline &amp; Flight Number</FieldLabel>
                    <Input
                      data-testid="field-return_airline_flight"
                      placeholder="e.g. Delta DL 416"
                      value={form.return_airline_flight}
                      onChange={(e) => update('return_airline_flight', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>Return Date &amp; Time (departing SJO)</FieldLabel>
                    <Input
                      data-testid="field-return_departure_datetime"
                      type="datetime-local"
                      value={form.return_departure_datetime}
                      onChange={(e) => update('return_departure_datetime', e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel required>Are you connecting?</FieldLabel>
                    <Select
                      value={form.return_connecting}
                      onValueChange={(v) => update('return_connecting', v)}
                    >
                      <SelectTrigger data-testid="field-return_connecting">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YES_NO.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <FieldLabel required>Home Arrival Date</FieldLabel>
                    <Input
                      data-testid="field-home_arrival_date"
                      type="date"
                      value={form.home_arrival_date}
                      onChange={(e) => update('home_arrival_date', e.target.value)}
                    />
                  </div>
                </div>
              </FormSection>
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          <Card className="border-2 border-forest/30">
            <CardContent className="p-6">
              <FormSection icon={FileText} title="Terms of Service & Privacy Policy">
                <p className="text-sm text-gray-600">
                  Please review the full agreement below. Scroll to the bottom,
                  then check the box to confirm.
                </p>
                <div
                  onScroll={handleTermsScroll}
                  className="h-64 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed"
                  data-testid="terms-scroller"
                >
                  {RETREAT_TERMS_TEXT}
                </div>
                <p className="text-xs text-gray-500">
                  {RETREAT_TERMS_REVISION}.
                  {!scrolledTerms && ' Scroll to the bottom to enable the agreement checkbox.'}
                </p>
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                    scrolledTerms ? 'border-forest/30 bg-sand/30' : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                >
                  <Checkbox
                    data-testid="terms-agreed"
                    checked={form.terms_agreed}
                    onCheckedChange={(v) => update('terms_agreed', !!v)}
                    disabled={!scrolledTerms}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-forest">
                    I have read and agree to the Sister Diamonds App Master
                    Terms of Service and Privacy Policy (
                    {RETREAT_TERMS_REVISION}).
                    My electronic acceptance constitutes a legally binding
                    agreement equivalent to a handwritten signature.
                  </span>
                </label>
              </FormSection>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button asChild variant="outline">
              <Link to="/retreat-welcome">Cancel</Link>
            </Button>
            <Button
              type="submit"
              data-testid="participant-form-submit"
              disabled={submitting}
              className="bg-terracotta hover:bg-terracotta/90"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
              ) : 'Submit My Info'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RetreatParticipantFormPage;
